import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, Loader2, Sparkles, Save, PencilLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/constants";

const copyText = (text) => {
  navigator.clipboard.writeText(text);
  toast.success("Kopiert");
};

const TextBlock = ({ title, text, onEdit, editable, testId }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(text || "");
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">{title}</CardTitle>
        <div className="flex gap-1">
          {editable && !editing && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setValue(text || ""); setEditing(true); }} data-testid={`${testId}-edit`}>Bearbeiten</Button>
          )}
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyText(text)} data-testid={`${testId}-copy`}><Copy className="h-3.5 w-3.5" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        {editing ? (
          <div className="space-y-2">
            <Textarea rows={6} value={value} onChange={(e) => setValue(e.target.value)} data-testid={`${testId}-textarea`} />
            <div className="flex gap-2">
              <Button size="sm" className="gap-1.5" onClick={() => { onEdit(value); setEditing(false); }} data-testid={`${testId}-save`}><Save className="h-3.5 w-3.5" /> Speichern</Button>
              <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Abbrechen</Button>
            </div>
          </div>
        ) : (
          <p className="text-sm whitespace-pre-line text-muted-foreground">{text || "–"}</p>
        )}
      </CardContent>
    </Card>
  );
};

export default function DescriptionTab({ vehicle, reload }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const d = vehicle.descriptions;
  const wasEdited = Boolean(d?.edited_at);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/descriptions/generate`);
      toast.success("Alle Textvarianten erstellt – nur bestätigte Angaben wurden verwendet");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const requestGenerate = () => {
    if (wasEdited) setConfirmOpen(true);
    else generate();
  };

  const edit = async (field, value) => {
    try {
      await api.patch(`/vehicles/${vehicle.id}/descriptions`, { updates: { [field]: value } });
      toast.success("Text aktualisiert");
      reload();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-5 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> KI-Texterstellung</div>
            <p className="text-xs text-muted-foreground mt-1">
              Erstellt alle Varianten in Ihrer Händler-Tonalität. Unbestätigte sensible Angaben (z.B. „unfallfrei“) werden strikt weggelassen.
              {d?.generated_at && <> · Zuletzt: {fmtDateTime(d.generated_at)}</>}
            </p>
            {wasEdited && (
              <p className="text-xs text-amber-300/90 mt-1 flex items-center gap-1.5" data-testid="description-edited-hint">
                <PencilLine className="h-3 w-3 shrink-0" /> Manuell bearbeitet ({fmtDateTime(d.edited_at)}{d.edited_by ? ` · ${d.edited_by}` : ""})
              </p>
            )}
          </div>
          <Button onClick={requestGenerate} disabled={busy} className="gap-2 font-semibold" data-testid="description-generate-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {d ? "Alle Texte neu generieren" : "Alle Texte generieren"}
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="description-overwrite-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Manuelle Änderungen gehen verloren</AlertDialogTitle>
            <AlertDialogDescription>
              Sie haben Inseratstexte manuell bearbeitet{d?.edited_at ? ` (zuletzt ${fmtDateTime(d.edited_at)})` : ""}.
              Beim Neu-Generieren werden alle Textfelder durch neue KI-Versionen ersetzt – Ihre manuellen
              Anpassungen werden dabei überschrieben.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="description-overwrite-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={generate} data-testid="description-overwrite-confirm">
              Trotzdem neu generieren
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {busy && !d && (
        <Card><CardContent className="py-10 text-center text-sm text-muted-foreground animate-pulse">Die KI erstellt Börsentitel, Beschreibungen, WhatsApp- und Social-Texte...</CardContent></Card>
      )}

      {d && (
        <div className="grid lg:grid-cols-2 gap-4" data-testid="description-results">
          <div className="space-y-4">
            <TextBlock title="Börsentitel" text={d.boersen_titel} editable onEdit={(v) => edit("boersen_titel", v)} testId="desc-titel" />
            <TextBlock title="Kurzbeschreibung" text={d.kurzbeschreibung} editable onEdit={(v) => edit("kurzbeschreibung", v)} testId="desc-kurz" />
            <TextBlock title="Ausführliche Beschreibung" text={d.beschreibung} editable onEdit={(v) => edit("beschreibung", v)} testId="desc-lang" />
            <TextBlock title="Emotionale Einleitung" text={d.emotionale_einleitung} editable onEdit={(v) => edit("emotionale_einleitung", v)} testId="desc-emo" />
          </div>
          <div className="space-y-4">
            <Card data-testid="desc-highlights">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Ausstattungs-Highlights</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-1.5">
                {(d.ausstattung_highlights || []).map((h, i) => (
                  <span key={i} className="rounded-full border border-primary/30 bg-primary/10 text-primary px-2.5 py-1 text-xs">{h}</span>
                ))}
              </CardContent>
            </Card>
            <TextBlock title="WhatsApp-Text" text={d.whatsapp_text} editable onEdit={(v) => edit("whatsapp_text", v)} testId="desc-whatsapp" />
            <TextBlock title="Social-Media-Text" text={d.social_media_text} editable onEdit={(v) => edit("social_media_text", v)} testId="desc-social" />
            <TextBlock title="Video-/Reel-Skript" text={d.reel_skript} editable onEdit={(v) => edit("reel_skript", v)} testId="desc-reel" />
            <Card data-testid="desc-argumente">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Verkäufer-Argumente (Top 5)</CardTitle></CardHeader>
              <CardContent className="space-y-1.5">
                {(d.verkaeufer_argumente || []).map((a, i) => (
                  <div key={i} className="flex gap-2 text-sm"><span className="font-display font-bold text-primary">{i + 1}.</span> <span className="text-muted-foreground">{a}</span></div>
                ))}
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
